import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const UTM_KEYS = [
	"utm_source",
	"utm_medium",
	"utm_campaign",
	"utm_term",
	"utm_content",
	"gclid",
	"fbclid",
];

const REFERRER_KEY = "referrer_url";

export const UtmManager: React.FC = () => {
	const location = useLocation();
	const navigate = useNavigate();

	useEffect(() => {
		const urlParams = new URLSearchParams(location.search);

		// Load stored UTMs + referrer
		let stored: Record<string, string> = {};
		try {
			const raw = sessionStorage.getItem("ace_utms") || "{}";
			stored = JSON.parse(raw);
		} catch {
			stored = {};
		}

		const updatedStored: Record<string, string> = { ...stored };
		let storageChanged = false;

		// Capture UTMs from URL into storage
		UTM_KEYS.forEach((key) => {
			const val = urlParams.get(key);
			if (val && val !== stored[key]) {
				updatedStored[key] = val;
				storageChanged = true;
			}
		});

		// Determine canonical referrer (same logic as Webflow script)
		const getCanonicalReferrer = (): string => {
			// Explicit referrer_url in URL (highest priority, can override stored)
			const refFromUrl = urlParams.get(REFERRER_KEY);
			if (refFromUrl) {
				return refFromUrl;
			}

			// If we already have a stored referrer, reuse it
			if (stored[REFERRER_KEY]) {
				return stored[REFERRER_KEY];
			}

			// Fallback: take browser referrer only if it's external
			const docRef = document.referrer;
			if (!docRef) return "";

			const a = document.createElement("a");
			a.href = docRef;

			const currentHost = window.location.hostname;

			// Same hostname -> internal navigation, ignore
			if (a.hostname === currentHost) {
				return "";
			}

			// External referrer (e.g., google, youtube, linkedin, marketing site, etc.)
			return docRef;
		};

		const canonicalReferrer = getCanonicalReferrer();

		if (canonicalReferrer && canonicalReferrer !== stored[REFERRER_KEY]) {
			updatedStored[REFERRER_KEY] = canonicalReferrer;
			storageChanged = true;
		}

		if (storageChanged) {
			try {
				sessionStorage.setItem("ace_utms", JSON.stringify(updatedStored));
			} catch {
				// ignore storage failures
			}
		}

		// Ensure current URL includes stored UTMs + canonical referrer
		const finalParams = new URLSearchParams(location.search);

		[...UTM_KEYS, REFERRER_KEY].forEach((key) => {
			const storedVal = updatedStored[key];
			if (storedVal && !finalParams.get(key)) {
				finalParams.set(key, storedVal);
			}
		});

		const finalSearch = finalParams.toString();
		const currentSearch = location.search.startsWith("?")
			? location.search.slice(1)
			: location.search;

		if (finalSearch === currentSearch) return;

		navigate(
			{
				pathname: location.pathname,
				search: finalSearch ? `?${finalSearch}` : "",
			},
			{ replace: true }
		);
	}, [location.pathname, location.search, navigate]);

	return null;
};

export default UtmManager;
